import { checkJSONResponse } from './templates/general/fetchUtils';

/**
 * Internationalization (i18n) class for handling translations in AEM applications.
 * Provides methods for loading translation dictionaries and retrieving localized text with placeholder replacement.
 * Supports automatic retry mechanisms for dictionary loading and pseudo-localization for testing.
 *
 * @example
 * // Basic translation
 * const i18n = new I18n();
 * await i18n.init();
 * const greeting = i18n.get('Hello World');
 *
 * @example
 * // Translation with placeholders
 * const message = i18n.get('User {0} has {1} messages', ['Alice', '5']);
 *
 */

class I18n {
    static LOCALE_DEFAULT = 'en';
    static PSEUDO_LANGUAGE = 'zz';
    static PSEUDO_PATTERN_KEY = '_pseudoPattern_';
    static MAX_RETRIES_DEFAULT = 2;

    /**
     * Creates a new I18n instance with optional configuration.
     *
     * @param {Object} [config={}] - Configuration options (optional)
     * @param {string} [config.locale] - Initial locale (defaults to document.documentElement.lang or 'en')
     * @param {string} [config.dictionaryUrlTemplate='/libs/cq/i18n/dict.{+locale}.json'] - URL template for dictionary files
     * @param {number} [config.maxRetries=2] - Maximum retry attempts for failed dictionary loads
     */
    constructor(config = {}) {
        this.locale = config.locale || document.documentElement.lang || I18n.LOCALE_DEFAULT;
        this.dictionaryUrlTemplate = config.dictionaryUrlTemplate || '/libs/cq/i18n/dict.{+locale}.json';
        this.dictionaries = {};
        this.pseudoTranslations = false;
        this.maxRetries = config.maxRetries || I18n.MAX_RETRIES_DEFAULT;
    }

    /**
     * Initializes the I18n instance by loading the dictionary for the current locale.
     *
     * @returns {Promise<void>}
     */
    async init() {
        this.dictionaries[this.locale] = await this._loadDictionarySync(this.locale, this.maxRetries);
    }

    /**
     * Gets the current locale.
     *
     * @returns {string} The current locale
     */
    getLocale() {
        return this.locale;
    }

    /**
     * Sets the current locale.
     *
     * @param {string} locale - The locale to set (defaults to 'en')
     */
    setLocale(locale = I18n.LOCALE_DEFAULT) {
        this.locale = locale;
    }

    /**
     * Sets the dictionary URL template.
     *
     * @param {string} template - The URL template for dictionary files
     */
    setDictionaryUrlTemplate(template) {
        this.dictionaryUrlTemplate = template;
    }

    /**
     * Resolves the dictionary URL for the specified locale.
     *
     * @param {string} locale - The locale to resolve URL for
     * @returns {string} The resolved dictionary URL
     * @private
     */
    _resolveUrl(locale) {
        return this.dictionaryUrlTemplate
            .replace('{locale}', encodeURIComponent(locale))
            .replace('{+locale}', locale);
    }

    /**
     * Loads the dictionary for the specified locale with retry mechanism.
     *
     * @param {string} locale - The locale to load dictionary for
     * @param {number} retryCount - Current retry attempt count
     * @returns {Promise<Object>} The loaded dictionary or empty object if failed
     * @private
     */
    async _loadDictionarySync(locale, retryCount = 0) {
        const url = this._resolveUrl(locale);
        const request = new Request(url, { cache: 'force-cache', redirect: 'error' });
        try {
            const response = await fetch(request);
            // Check response for valid JSON and content type.
            const json = await checkJSONResponse(response);
            if (json && Object.keys(json).length > 0) {
                return json;
            } else if (retryCount < this.maxRetries) {
                return this._loadDictionarySync(locale, retryCount + 1);
            }
            return json;
        } catch (e) {
            console.warn(`I18n: Failed to load dictionary for ${locale} (attempt ${retryCount + 1})`, e);
            if (retryCount < this.maxRetries) {
                return this._loadDictionarySync(locale, retryCount + 1);
            }
            return {};
        }
    }

    /**
     * Replaces placeholders in text with provided values.
     *
     * @param {string} text - The text containing placeholders
     * @param {Array|string} snippets - Values to replace placeholders with
     * @returns {string} The text with replaced placeholders
     * @private
     */
    _patchText(text, snippets) {
        if (!snippets) {
            return text;
        }

        let patchedText = text;

        if (Array.isArray(snippets)) {
            snippets.forEach((val, i) => {
                const placeholder = `{${i}}`;
                patchedText = patchedText.split(placeholder).join(String(val));
            });
        } else {
            patchedText = patchedText.split('{0}').join(String(snippets));
        }

        return patchedText;
    }

    /**
     * Retrieves the translation for the given text with optional placeholder replacement.
     *
     * @param {string} text - The text to translate
     * @param {Array|string} snippets - Values for placeholder replacement (optional)
     * @param {string} note - Context note for translation (optional)
     * @returns {string} The translated text or original text if no translation found
     */
    get(text, snippets, note) {
        if (!text) {
            return '';
        }

        const locale = this.getLocale();
        const dict = this.dictionaries[locale];

        let key;
        if (this.pseudoTranslations) {
            key = I18n.PSEUDO_PATTERN_KEY;
        } else if (note) {
            key = `${text} ((${note}))`;
        } else {
            key = text;
        }

        let translation = dict[key] || text;

        if (this.pseudoTranslations) {
            translation = translation
                .replace('{string}', text)
                .replace('{comment}', note || '');
        }

        return this._patchText(translation, snippets);
    }

    /**
     * Retrieves the translation for the given text with optional context note.
     *
     * @param {string} text - The text to translate
     * @param {string} note - Context note for translation (optional)
     * @returns {string|null} The translated text or null if text is empty
     */
    getVar(text, note) {
        return text ? this.get(text, null, note) : null;
    }
}

const i18n = new I18n({
    maxRetries: 2
});
await i18n.init();

export { i18n };
